import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
  Checkbox,
  IconButton,
  ListItemButton,
  ListSubheader,
  Typography,
} from "@mui/material";
import { isWithinDateRange } from "../../../admin/share/helper";
import useDebouncedFunction from "../../../helperJs/debounce";
import AdminZustandStore from "../../../admin/AdminZustandStore";
import CompareByDate from "./compareByDate";
import dayjs from "dayjs";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function FilterDrawer({ data, setFilterData }) {
  const anchor = "right";
  const date1DayAgo = dayjs().startOf("day");
  const date30DaysAgo = dayjs().subtract(30, "day");
  const sixMonthsAgo = dayjs("0000-01-01");

  let oneDayBefore = date1DayAgo.format("YYYY-MM-DD");
  let thirydaysBefore = date30DaysAgo.format("YYYY-MM-DD"); // Format the date

  const [ShowDate, setShowDate] = React.useState(false);
  const [Open, setOpen] = React.useState(false);
  const {
    checked,
    setChecked,
    filterByDate,
    setFilterByDate,
    resetFilterByDate,
    resetChecked,
  } = AdminZustandStore();
  const [selectedDate, setSelectedDate] = React.useState({
    from: dayjs(thirydaysBefore),
    to: dayjs(oneDayBefore),
  });
  const fromDate = dayjs(selectedDate.from).format("YYYY-MM-DD");
  const toDate = dayjs(selectedDate.to).format("YYYY-MM-DD");

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpen(open);
  };

  // Memoize `checked` to avoid unnecessary re-renders
  const memoizedChecked = React.useMemo(() => checked, [checked]);

  // Memoize `data` if it's derived from props or computed dynamically
  const memoizedData = React.useMemo(() => data, [data]);

  const filterKey = React.useMemo(() => {
    return [
      {
        label: "Project",
        dataKey: "no_of_projects",
        key: ["1-50", "51-100", "100-150", "150+"],
      },
      {
        label: "Keyword",
        dataKey: "no_of_keywords",
        key: ["0-500", "500-1000", "1000+"],
      },
      {
        label: "Paid",
        dataKey: "paid",
        key: ["$0", "$10", "$19", "$39", "$99", "Referral"],
      },
      {
        label: "Plan",
        dataKey: "subscription",
        key: ["Free", "pro", "elite", "lifetime", "ultimate", "Solo"],
      },
      {
        label: "Account",
        dataKey: "acc_owner",
        key: ["Sub-User"],
      },
      {
        label: "Tool",
        dataKey: "tools",
        key: ["Ahrefs", "Semrush", "Mangools", "SE Ranking", "SpyFu", "Other"],
      },
      {
        label: "Organisation",
        dataKey: "organisation",
        key: [
          "An SEO agency",
          "A marketing agency",
          "A marketing department at a bigger company",
          "Myself (I own a niche blog)",
          "Myself (I own a small business)",
          "A large media company",
        ],
      },
      {
        label: "Referral",
        dataKey: "referral",
        key: [
          "Google Search",
          "Social media",
          "Online advertising",
          "Website or blog",
          "Email marketing",
          "Forum or online community",
          "Existing User",
          "Other",
        ],
      },
      {
        label: "Last Login",
        dataKey: "last_login",
        key: [
          "Last One Week",
          "Last One Month",
          "Last 75 Days",
          "Above 75 Days",
        ],
      },
    ];
  }, []);

  const debouncedFilterDataFunction = useDebouncedFunction(() => {
    const isNoFilterSelected = Object.values(memoizedChecked).every(
      (selected) => selected.length === 0
    );

    if (isNoFilterSelected && !filterByDate.filter) {
      setFilterData({
        filter: false,
        data: [],
      });
      return;
    }

    const table_data = filterByDate.filter ? filterByDate.data : data;
    const filter_data = table_data?.filter((item) => {
      // return filterKey.some((filter) => {
      return filterKey.every((filter) => {
        const selectedValues = memoizedChecked[filter.label] || [];
        if (selectedValues.length === 0) return true; // If no filter is selected, include all data
        // if (selectedValues.length === 0) return false; // If no filter is selected, include all data

        const itemValue = item[filter.dataKey];

        if (filter.label === "Account") {
          // If "Sub-User" is selected, show only false values
          return !selectedValues.includes("Sub-User") || itemValue === false;
        }

        if (filter.label === "Referral") {
          const excludeValues = [
            "Google Search",
            "Social media",
            "Online advertising",
            "Website or blog",
            "Email marketing",
            "Forum or online community",
            "Existing User",
          ];
          // If "Other" is checked, we want to include all values except for the ones in excludeValues
          if (selectedValues.includes("Other")) {
            return !excludeValues.includes(itemValue); // Exclude specific values
          }

          // For other selections (non-"Other"), apply normal inclusion
          return selectedValues.includes(itemValue);
        }

        if (filter.label === "Project" || filter.label === "Keyword") {
          // Range-based filters (e.g., "1-50", "500-1000")
          return selectedValues.some((selected) => {
            if (selected === "150+") {
              return parseInt(itemValue) > 150;
            }
            if (selected === "1000+") {
              return parseInt(itemValue) > 1000;
            }
            const [min, max] = selected.split("-").map(Number);
            return parseInt(itemValue) >= min && parseInt(itemValue) <= max;
          });
        }

        if (filter.label === "Last Login") {
          // Date-based filter (e.g., "Last One Week")
          return selectedValues.some((selected) =>
            isWithinDateRange(itemValue, selected)
          );
        }

        // String-based filters (e.g., "Paid", "Plan", "Tool")
        return selectedValues.includes(itemValue);
      });
    });
    setFilterData({
      filter: true,
      data: filter_data,
    });
  }, 300); //500 ms debounce delay

  React.useEffect(() => {
    debouncedFilterDataFunction(memoizedChecked);
  }, [memoizedData, memoizedChecked, filterByDate]);

  const handleFilterOption = (type, value) => {
    setChecked(type, value);
  };

  const handleResetFilter = () => {
    resetChecked();
    resetFilterByDate();
  };
  const handleDateRefresh = () => {
    setFilterByDate(false, "", []);
  };

  const list = () => (
    <Box sx={{ width: 250 }} role="presentation">
      <Box className="d-flex w-100 justify-content-between align-items-center">
        <Typography variant="h6" className="m-2">
          Filters
        </Typography>
        <Button
          variant="contained"
          className="btn_1 mx-2"
          onClick={handleResetFilter}
          sx={{
            height: "35px",
          }}
        >
          Reset
        </Button>
      </Box>
      <Divider />

      <List subheader={<ListSubheader>Compare By Date</ListSubheader>}>
        <ListItem
          key={"compare-by-date"}
          className="point"
          disablePadding
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="refresh"
              onClick={handleDateRefresh}
            >
              <RefreshIcon />
            </IconButton>
          }
        >
          <ListItemButton onClick={() => setShowDate((pre) => !pre)}>
            <ListItemText
              primary="Select Date"
              secondary={filterByDate.date || null}
            />
          </ListItemButton>
        </ListItem>
      </List>
      {filterKey.map((filter) => {
        return (
          <React.Fragment key={filter.label}>
            <List subheader={<ListSubheader>{filter.label}</ListSubheader>}>
              {filter?.key?.map((item) => {
                const labelId = `checkbox-list-label-${filter.label}-${item}`;
                const isChecked = checked[filter.label]?.includes(item);
                return (
                  <ListItem
                    key={item}
                    className="point"
                    disablePadding
                    onClick={() => handleFilterOption(filter.label, item)}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Checkbox
                        checked={isChecked ?? false}
                        tabIndex={-1}
                        disableRipple
                        readOnly
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={item}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </ListItem>
                );
              })}
            </List>
            <Divider sx={{ borderColor: "#0000004D" }} />
          </React.Fragment>
        );
      })}
      <Divider />
    </Box>
  );

  const handleClose = () => {
    setShowDate(false);
  };

  const handleDateChange = async () => {
    const filter_data = data?.filter((item) => {
      const itemDate = dayjs(item.created_at).format("YYYY-MM-DD");
      return itemDate >= fromDate && itemDate <= toDate;
    });

    setFilterByDate(true, `${fromDate} - ${toDate}`, filter_data);
    setShowDate(false);
  };

  return (
    <>
      {ShowDate && (
        <CompareByDate
          {...{
            handleClose,
            handleDateChange,
            selectedDate,
            setSelectedDate,
            sixMonthsAgo,
            date1DayAgo,
          }}
        />
      )}
      <Button
        onClick={toggleDrawer(true)}
        variant="contained"
        color="secondary"
        sx={{
          backgroundColor: "#365486",
          "&:hover": {
            backgroundColor: "#365486",
          },
        }}
        className="ms-2"
      >
        Filter
      </Button>
      <Drawer anchor={anchor} open={Open} onClose={toggleDrawer(false)}>
        {list(anchor)}
      </Drawer>
    </>
  );
}
